import Head from "next/head";
import { useLocale } from "../../i18n/LocaleContext";
import {
  LOCALES,
  OG_LOCALE,
  SITE_URL,
  absoluteUrl,
  localePath,
} from "../../i18n/config";

const IMAGE_URL = `${SITE_URL}/img/pokegrid-social.png`;
const ROUTE_PATHS = { home: "/", privacy: "/privacy" };

export function SeoHead() {
  const { locale, route, t } = useLocale();

  const title = t(`seo.${route}.title`);
  const description = t(`seo.${route}.description`);

  // Date pages are noindex, so they get no canonical or hreflang
  const path = ROUTE_PATHS[route];
  const url = path ? absoluteUrl(localePath(locale, path)) : null;

  return (
    <Head>
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />

      {url ? (
        <>
          <link rel="canonical" href={url} />
          {LOCALES.map((alternate) => (
            <link
              key={`hreflang-${alternate}`}
              rel="alternate"
              hrefLang={alternate}
              href={absoluteUrl(localePath(alternate, path))}
            />
          ))}
          <link rel="alternate" hrefLang="x-default" href={absoluteUrl(path)} />
          <meta property="og:url" content={url} />
          <meta name="twitter:url" content={url} />
        </>
      ) : (
        <meta name="robots" content="noindex,follow" />
      )}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="PokeGrid" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={IMAGE_URL} />
      <meta property="og:locale" content={OG_LOCALE[locale]} />
      {LOCALES.filter((alternate) => alternate !== locale).map((alternate) => (
        <meta
          key={`og-locale-${alternate}`}
          property="og:locale:alternate"
          content={OG_LOCALE[alternate]}
        />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={IMAGE_URL} />

      {route === "home" && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(jsonLd(url, title, description, locale)),
          }}
        />
      )}
    </Head>
  );
}

function jsonLd(url, title, description, locale) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        name: "PokeGrid",
        url: `${SITE_URL}/`,
      },
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: title,
        description,
        inLanguage: locale,
        isPartOf: { "@id": `${SITE_URL}/#website` },
      },
    ],
  };
}
